import React, { useState } from "react";
import {
  TextField,
  Button,
  Typography,
  Tooltip,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CloseIcon from "@mui/icons-material/Close";

const Instruction = ({
  title,
  addInstructionStep,
  toggleItem,
  name,
  template,
  contents = {},
  imageSrc,
  onNameChange,
  onTemplateChange,
  onContentChange,
  onImageChange,
}) => {
  const allLanguages = [
    "pl","cs","da","fi","no","sv","de","nl","be","fr","es","it","ja","ko","th","mx","ru",
    "hr","sr","pt","br","cl","ar","zh","tr","cn","vi"
  ];

  const [addedLanguages, setAddedLanguages] = useState(
    Object.keys(contents).filter((l) => l !== "default")
  );
  const [newLang, setNewLang] = useState("");
  const [errors, setErrors] = useState({}); 

  const handleAddLanguage = () => {
    if (newLang && !addedLanguages.includes(newLang)) {
      setAddedLanguages([...addedLanguages, newLang]);
      setNewLang("");
    }
  };

  const handleRemoveLanguage = (lang) => {
    setAddedLanguages(addedLanguages.filter((l) => l !== lang));
    onContentChange(lang, ""); 
    setErrors((prev) => {
      const copy = { ...prev };
      delete copy[lang];
      return copy;
    });
  };

  const handleSubmit = () => {
    let newErrors = {};
    addedLanguages.forEach((lang) => {
      if (!contents[lang] || contents[lang].trim() === "") {
        newErrors[lang] = "Please fill in this field";
      }
    });

    if (!contents.default || contents.default.trim() === "") {
      newErrors.default = "Default content is required";
    }

    if (!name || name.trim() === "") {
      newErrors.name = "Step name is required";
    }

    if (!template || template.trim() === "") {
      newErrors.template = "Template is required";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      addInstructionStep();
    }
  };

  const availableLanguages = allLanguages.filter(
    (lang) => !addedLanguages.includes(lang)
  );

  return (
    <div style={{ padding: "0px 0px 24px", position: "relative" }}>
      <div style={{ position: "absolute", left: 16 }}>
        <Tooltip title="Back to table">
          <IconButton onClick={toggleItem} aria-label="back" size="large">
            <ArrowBackRoundedIcon />
          </IconButton>
        </Tooltip>
      </div>

      <div
        style={{
          margin: "auto",
          width: "min(720px, 100%)",
          display: "grid",
          gap: 20,
          textAlign: "center",
        }}
      >
        <Typography
          style={{
            paddingTop: 16,
            fontSize: 22,
            fontWeight: 600,
          }}
          component="span"
        >
          {title}
        </Typography>

        <TextField
          label="Step name"
          value={name}
          onChange={onNameChange}
          variant="standard"
          fullWidth
          error={!!errors.name}
          helperText={errors.name}
        />
        <TextField
          label="Template"
          value={template}
          onChange={(e) => onTemplateChange(e)} 
          variant="standard"
          fullWidth
          error={!!errors.template}
          helperText={errors.template}
        />

        <TextField
          label="Content (default)"
          value={contents.default || ""}
          onChange={(e) => onContentChange("default", e.target.value)}
          variant="standard"
          fullWidth
          multiline
          error={!!errors.default}
          helperText={errors.default}
        />

        <TextField
          label="Image source"
          value={imageSrc}
          onChange={onImageChange}
          variant="standard"
          fullWidth
        />

        <div style={{ marginTop: 12, textAlign: "left" }}>
          <Typography
            variant="subtitle2"
            style={{ fontWeight: 600, marginBottom: 8 }}
          >
            Add language
          </Typography>

          {addedLanguages.map((lang) => (
            <div
              key={lang}
              style={{
                position: "relative",
                marginBottom: 16,
              }}
            >
              <TextField
                label={`Content (${lang})`}
                value={contents[lang] || ""}
                onChange={(e) => onContentChange(lang, e.target.value)}
                variant="standard"
                fullWidth
                multiline
                error={!!errors[lang]}
                helperText={errors[lang]}
              />
              <IconButton
                size="small"
                onClick={() => handleRemoveLanguage(lang)}
                style={{
                  position: "absolute",
                  top: 0,
                  right: -8,
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          ))}

          {availableLanguages.length > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginTop: 8,
              }}
            >
              <FormControl variant="standard" style={{ flex: 1 }}>
                <InputLabel>Add language</InputLabel>
                <Select
                  value={newLang}
                  onChange={(e) => setNewLang(e.target.value)}
                >
                  {availableLanguages.map((lang) => (
                    <MenuItem key={lang} value={lang}>
                      {lang}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                onClick={handleAddLanguage}
                disabled={!newLang}
                style={{
                  marginLeft: "auto",
                  marginTop: 12,
                }}
              >
                Add
              </Button>
            </div>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
          <Button
            style={{ background: "#00badb", minWidth: 220 }}
            variant="contained"
            size="large"
            onClick={handleSubmit}
          >
            {title}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Instruction;
